import { useState, useCallback } from 'react';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';
import { Tables } from '@/integrations/supabase/types';
import { PostgrestError } from '@supabase/supabase-js';

export type OfferTemplate = Tables<'offer_templates'>;

export const useOfferTemplates = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [offerTemplates, setOfferTemplates] = useState<OfferTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOfferTemplates = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('offer_templates')
        .select('*')
        .order('offer_name', { ascending: true });

      if (error) throw error;
      setOfferTemplates(data as OfferTemplate[]);

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to fetch offer templates: ${err.message}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast]);

  const createOfferTemplate = useCallback(async (templateData: Omit<OfferTemplate, 'offer_id' | 'created_at' | 'updated_at'>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return null;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('offer_templates')
        .insert([templateData])
        .select()
        .single();
      
      if (error) throw error;
      
      toast({ title: "Success", description: "Offer template created successfully." });
      fetchOfferTemplates(); // Refresh the list
      return data as OfferTemplate;

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to create offer template: ${err.message}`, variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast, fetchOfferTemplates]);

  const updateOfferTemplate = useCallback(async (offer_id: string, updates: Partial<OfferTemplate>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('offer_templates')
        .update(updates)
        .eq('offer_id', offer_id);

      if (error) throw error;

      toast({ title: "Success", description: "Offer template updated successfully." });
      fetchOfferTemplates(); // Refresh the list

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to update offer template: ${err.message}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast, fetchOfferTemplates]);

  const deleteOfferTemplate = useCallback(async (offer_id: string) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('offer_templates')
        .delete()
        .eq('offer_id', offer_id);

      if (error) throw error;

      toast({ title: "Success", description: "Offer template deleted successfully." });
      fetchOfferTemplates(); // Refresh the list

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to delete offer template: ${err.message}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast, fetchOfferTemplates]);


  return { offerTemplates, loading, error, fetchOfferTemplates, createOfferTemplate, updateOfferTemplate, deleteOfferTemplate };
};